import type { GetPlansOverviewsResponse } from '@vy/api-definition';
import { GetPlansOverviewsResponseSchema } from '@vy/api-definition';
import { isNotEmptyString, purge, vySerialize, withDefault } from '@vy/utils';
import type { QueryClient } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import { usePlansOverviewsStatusesParam } from '../../../pages/plans/PlansPage/plans-page-components/hooks/use-plans-overviews-statuses-param';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';
import { useSearchWithSearchParams } from '../../use-search-with-search-params';
import { useTableSorting } from '../../use-table-sorting';
import type { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';
import { PLANS_OVERVIEWS_PAGE_SIZE } from './use-plans-overviews-consts';

type UsePlansOverviewsPayload = Parameters<ReturnType<typeof getPrivateApi>['getPlanOverviews']>[0];

export function usePlansOverviews(
  payload: UsePlansOverviewsPayload = {},
  options?: CustomUseQueryOptions<GetPlansOverviewsResponse>,
) {
  const queryClient = useQueryClient();

  const queryKeyDependencies = purge(Object.values(payload));
  const queryKey = queryKeys.plansOverviews(...queryKeyDependencies);

  return useQuery(
    queryKey,
    async ({ signal }) => {
      const api = getPrivateApi();
      await cancelGetPlansOverviewsQueries(queryClient);

      const getPlansOverviewsResponse = await api.getPlanOverviews(
        {
          ...payload,
          pageSize: withDefault(payload.pageSize, PLANS_OVERVIEWS_PAGE_SIZE),
          page: withDefault(payload.page, 0),
        },
        { signal },
      );

      return GetPlansOverviewsResponseSchema.parse(getPlansOverviewsResponse);
    },
    {
      keepPreviousData: true,
      ...options,
    },
  );
}

async function cancelGetPlansOverviewsQueries(queryClient: QueryClient) {
  const resource = queryKeys.plansOverviews()[0];

  await queryClient.cancelQueries({
    predicate: (query) => query.queryKey[0] === resource,
  });
}

export function usePlansOverviewsWithPageParams(
  pagination: { page: number; pageSize: number },
  options?: CustomUseQueryOptions<GetPlansOverviewsResponse>,
) {
  const { page, pageSize } = pagination;
  const [plansOverviewsStatuses, _setPlansOverviewsStatuses] = usePlansOverviewsStatusesParam();
  const [query] = useSearchWithSearchParams();
  const { currentSortModel } = useTableSorting();

  return usePlansOverviews(
    {
      page,
      pageSize,
      search: isNotEmptyString(query) ? query : undefined,
      planStatuses: vySerialize(withDefault(plansOverviewsStatuses, [])),
      sort: vySerialize(currentSortModel),
    },
    options,
  );
}
